import React, { useEffect, useState, useCallback } from 'react';
import { Typography, Box, CircularProgress, Alert, Grid, Paper, Chip, Tooltip, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, Snackbar, ToggleButtonGroup, ToggleButton, TableContainer, Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'; // Correction pour date-fns v2
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { format, startOfWeek, endOfWeek, eachDayOfInterval } from 'date-fns';
import frLocale from 'date-fns/locale/fr';
import ElectricCarIcon from '@mui/icons-material/ElectricCar';
import AccessibleIcon from '@mui/icons-material/Accessible';
import TwoWheelerIcon from '@mui/icons-material/TwoWheeler';
import PersonIcon from '@mui/icons-material/Person';
import { useAuth } from '../components/AuthContext.tsx';
import { authenticatedApiCall } from '../api.ts';

interface User {
  id: number;
  first_name: string;
  last_name: string;
}

interface Assignment {
  id: number;
  user_id: number;
  spot_id: number;
  user: User; // Add user details to assignment
  parking_spot: ParkingSpot; // Add spot details to assignment
  start_time: string;
  end_time: string | null;
}

interface ParkingSpot {
  id: number;
  spot_number: string;
  has_electric_charger: boolean;
  is_pmr_accessible: boolean;
}

interface MotorcycleCheckin {
  id: number;
  user_id: number;
  user: User;
}

interface MotorcycleCheckinStatus {
  has_checkin: boolean;
}

function Dashboard() {
  const { user, token, isAuthenticated, isAdmin } = useAuth(); // user from useAuth is the currently logged in user
  const [spots, setSpots] = useState<ParkingSpot[]>([]);
  const [motorcycleCheckins, setMotorcycleCheckins] = useState<MotorcycleCheckin[]>([]);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<'daily' | 'weekly'>('daily');
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  
  // State for reservation modal
  const [spotToReserve, setSpotToReserve] = useState<ParkingSpot | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // State for cancellation modal
  const [assignmentToCancel, setAssignmentToCancel] = useState<Assignment | null>(null);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

  // State for motorcycle modal
  const [isMotorcycleModalOpen, setIsMotorcycleModalOpen] = useState(false);
  const [motorcycleAction, setMotorcycleAction] = useState<'checkin' | 'checkout' | null>(null);

  // State for snackbar notification
  const [snackbar, setSnackbar] = useState<{ open: boolean, message: string, severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  const fetchParkingData = useCallback(async (date: Date | null) => {
    if (!date) return;
    try {
      setLoading(true);
      // Réinitialiser les données pour éviter les incohérences lors du changement de date
      setMotorcycleCheckins([]);

      let url = '/assignments/';
      if (view === 'daily') {
        url += `?start_date=${format(date, 'yyyy-MM-dd')}`;
      } else {
        const weekStart = startOfWeek(date, { weekStartsOn: 1 });
        const weekEnd = endOfWeek(date, { weekStartsOn: 1 });
        url += `?start_date=${format(weekStart, 'yyyy-MM-dd')}&end_date=${format(weekEnd, 'yyyy-MM-dd')}`;
      }

      // Fetch spots and assignments in parallel. User data is now included in assignments.
      const [spotsData, assignmentsData, motorcycleData] = await Promise.all([
        authenticatedApiCall('GET', '/parking_spots/', token),
        authenticatedApiCall('GET', url, token),
        // On récupère toujours les données moto pour le jour sélectionné, peu importe la vue
        authenticatedApiCall('GET', `/motorcycle-checkins/?date=${format(date, 'yyyy-MM-dd')}`, token), 
      ]);

      setSpots(spotsData);
      setAssignments(assignmentsData);
      setMotorcycleCheckins(motorcycleData);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Échec de la récupération des données du parking');
    } finally {
      setLoading(false);
    }
  }, [token, view]);

  useEffect(() => {
    if (isAuthenticated && token) {
      if (selectedDate) {
        fetchParkingData(selectedDate);
      }
    } else if (!isAuthenticated) {
      setError('Vous devez être connecté pour voir le tableau de bord.');
      setLoading(false);
    }
  }, [token, isAuthenticated, selectedDate, fetchParkingData]);

  const handleSpotClick = (spot: ParkingSpot, assignment: Assignment | undefined) => {
    const isMyReservation = assignment?.user_id === user?.id;

    // Règle : Empêcher la réservation si un enregistrement moto existe déjà pour ce jour.
    if (!assignment && !isAdmin && userHasMotorcycleCheckin) {
      setSnackbar({ open: true, message: "Vous avez déjà un enregistrement pour votre moto à cette date. Vous ne pouvez pas réserver une place de parking.", severity: 'error' });
      return;
    }

    if (assignment && (isAdmin || isMyReservation)) {
      // Open cancellation modal
      setAssignmentToCancel(assignment);
      setIsCancelModalOpen(true);
    } else if (!assignment && (isAdmin || !userHasReservation)) {
      // Open reservation modal
      setSpotToReserve(spot);
      setIsModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSpotToReserve(null);
  };

  const handleConfirmReservation = async () => {
    if (!spotToReserve || !selectedDate) return;

    try {
      await authenticatedApiCall('POST', '/assignments/', token, {
        spot_id: spotToReserve.id,
        date: format(selectedDate, 'yyyy-MM-dd'),
      });
      setSnackbar({ open: true, message: `Place ${spotToReserve.spot_number} réservée avec succès !`, severity: 'success' });
      fetchParkingData(selectedDate); // Refresh data
    } catch (err: any) {
      setSnackbar({ open: true, message: err.message || 'Erreur lors de la réservation.', severity: 'error' });
    } finally {
      handleCloseModal();
    }
  };

  const handleCloseCancelModal = () => {
    setIsCancelModalOpen(false);
    setAssignmentToCancel(null);
  };

  const handleConfirmCancellation = async () => {
    if (!assignmentToCancel || !selectedDate) return;

    try {
      await authenticatedApiCall('DELETE', `/assignments/${assignmentToCancel.id}`, token);
      setSnackbar({ open: true, message: `Réservation pour la place ${assignmentToCancel.parking_spot.spot_number} annulée.`, severity: 'success' });
      fetchParkingData(selectedDate); // Refresh data
    } catch (err: any) {
      setSnackbar({ open: true, message: err.message || "Erreur lors de l'annulation.", severity: 'error' });
    } finally {
      handleCloseCancelModal();
    }
  };

  const handleMotorcycleClick = async () => {
    if (!selectedDate || !user?.has_motorcycle) return;
    try {
      const formattedDate = format(selectedDate, 'yyyy-MM-dd');
      const status: MotorcycleCheckinStatus = await authenticatedApiCall('GET', `/motorcycle-checkins/me?date=${formattedDate}`, token);
      
      setMotorcycleAction(status.has_checkin ? 'checkout' : 'checkin');
    } catch (err: any) {
      setSnackbar({ open: true, message: err.message || "Erreur lors de la vérification du statut.", severity: 'error' });
      return; // Stop if we can't verify status
    }
    setIsMotorcycleModalOpen(true);
  };

  const handleCloseMotorcycleModal = () => {
    setIsMotorcycleModalOpen(false);
    setMotorcycleAction(null);
  };

  const handleConfirmMotorcycleAction = async () => {
    if (!selectedDate || !motorcycleAction) return;

    const formattedDate = format(selectedDate, 'yyyy-MM-dd');

    try {
      if (motorcycleAction === 'checkout') {
        // Pour DELETE, on passe la date en query param
        setMotorcycleCheckins(prev => prev.filter(c => c.user_id !== user?.id)); // Mise à jour optimiste
        // La requête DELETE renvoie un statut 204 (No Content), donc on ne traite pas la réponse JSON.
        await authenticatedApiCall('DELETE', `/motorcycle-checkins/?date=${formattedDate}`, token, undefined, true);
        setSnackbar({ open: true, message: "Vous n'êtes plus enregistré pour ce jour.", severity: 'success' });
      } else if (motorcycleAction === 'checkin') {
        const newCheckin = await authenticatedApiCall('POST', '/motorcycle-checkins/', token, { date: formattedDate });
        setMotorcycleCheckins(prev => [...prev, newCheckin]); // Mise à jour optimiste
        setSnackbar({ open: true, message: 'Vous êtes enregistré pour ce jour !', severity: 'success' });
      }
    } catch (err: any) {
      setSnackbar({ open: true, message: err.message || "Erreur lors de l'enregistrement.", severity: 'error' });
      // En cas d'erreur, on rafraîchit les données pour revenir à un état cohérent
      if (selectedDate) fetchParkingData(selectedDate);
    } finally {
      handleCloseMotorcycleModal();
    }
  };

  const handleViewChange = (event: React.MouseEvent<HTMLElement>, newView: 'daily' | 'weekly') => {
    if (newView !== null) {
      setView(newView);
    }
  };

  // Filtre les réservations pour ne prendre en compte que le jour sélectionné, même en vue hebdomadaire
  const assignmentsForSelectedDate = selectedDate 
    ? assignments.filter(a => format(new Date(a.start_time), 'yyyy-MM-dd') === format(selectedDate, 'yyyy-MM-dd'))
    : [];
  const userHasReservation = assignmentsForSelectedDate.some(a => a.user_id === user?.id);
  const userHasMotorcycleCheckin = motorcycleCheckins.some(c => c.user_id === user?.id);

  const assignmentsBySpotId = new Map<number, Assignment>();
  // Ensure assignments is an array before iterating
  Array.isArray(assignments) && assignments.forEach(a => {
    assignmentsBySpotId.set(a.spot_id, a);
  });

  const weekDays = selectedDate ? eachDayOfInterval({
    start: startOfWeek(selectedDate, { weekStartsOn: 1 }),
    end: endOfWeek(selectedDate, { weekStartsOn: 1 }),
  }) : [];

  const assignmentsBySpotAndDate = new Map<string, Assignment>();
  Array.isArray(assignments) && assignments.forEach(a => {
    assignmentsBySpotAndDate.set(`${a.spot_id}-${format(new Date(a.start_time), 'yyyy-MM-dd')}`, a);
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (spots.length === 0) {
    return (
      <Alert severity="info">
        Aucune place de parking n'a été configurée.
        <br />
        Un administrateur doit ajouter des places depuis la page "Gestion des Places".
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" sx={{ mb: 2 }}>
        État du Parking
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
        <ToggleButtonGroup
          value={view}
          exclusive
          onChange={handleViewChange}
          aria-label="text alignment"
        >
          <ToggleButton value="daily" aria-label="Vue journalière">Journalière</ToggleButton>
          <ToggleButton value="weekly" aria-label="Vue hebdomadaire">Hebdomadaire</ToggleButton>
        </ToggleButtonGroup>

      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={frLocale}>
        <DatePicker
          label="Sélectionner une date"
          value={selectedDate}
          onChange={(newValue) => setSelectedDate(newValue)}
          sx={{ mb: 4 }}
        />
      </LocalizationProvider>
      </Box>

      <Grid container spacing={2}>
        {/* Motorcycle Spot */}
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <Tooltip title={isAdmin ? motorcycleCheckins.map(c => `${c.user.first_name} ${c.user.last_name}`).join(', ') : ''}>
            <Paper
              elevation={3}
              onClick={handleMotorcycleClick}
              sx={{
                p: 2,
                textAlign: 'center',
                backgroundColor: userHasMotorcycleCheckin ? 'secondary.main' : 'grey.700',
                color: 'white',
                position: 'relative',
                minHeight: 120,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                cursor: user?.has_motorcycle ? 'pointer' : 'not-allowed',
                opacity: user?.has_motorcycle ? 1 : 0.5,
              }}
            >
              <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                <TwoWheelerIcon />
              </Box>
              <Typography variant="h5" component="div">
                Motos
              </Typography>
              <Chip
                icon={<PersonIcon />}
                label={`${motorcycleCheckins.length} enregistré(s)`}
                variant="outlined"
                sx={{ mt: 1, backgroundColor: 'rgba(255,255,255,0.2)' }}
              />
            </Paper>
          </Tooltip>
        </Grid>

        {spots.sort((a, b) => a.spot_number.localeCompare(b.spot_number, undefined, { numeric: true })).map((spot) => {
          const assignment = assignmentsBySpotId.get(spot.id);
          const isOccupied = !!assignment;
          const isMyReservation = assignment?.user_id === user?.id;
          const canInteract = !isOccupied ? (isAdmin || !userHasReservation) : (isAdmin || isMyReservation);

          return (
            <Grid item xs={12} sm={6} md={4} lg={3} key={spot.id}>
              <Paper
                elevation={3}
                onClick={() => canInteract && handleSpotClick(spot, assignment)}
                sx={{
                  p: 2,
                  textAlign: 'center',
                  backgroundColor: isMyReservation ? 'primary.light' : (isOccupied ? 'error.light' : 'success.light'),
                  color: 'white',
                  position: 'relative',
                  minHeight: 120,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  cursor: canInteract ? 'pointer' : 'not-allowed',
                  opacity: canInteract ? 1 : 0.6,
                }}
              >
                <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 0.5 }}>
                  {spot.has_electric_charger && <Tooltip title="Borne électrique"><ElectricCarIcon fontSize="small" /></Tooltip>}
                  {spot.is_pmr_accessible && <Tooltip title="Accès PMR"><AccessibleIcon fontSize="small" /></Tooltip>}
                </Box>
                <Typography variant="h5" component="div">
                  {spot.spot_number}
                </Typography>
                <Chip
                  icon={isOccupied ? <PersonIcon /> : undefined}
                  label={isMyReservation ? 'Ma réservation' : (isOccupied ? 'Occupé' : 'Libre')}
                  color={isOccupied ? 'error' : 'success'}
                  variant="outlined"
                  sx={{ mt: 1, backgroundColor: 'rgba(255,255,255,0.2)' }}
                />
                {isOccupied && isAdmin && assignment.user && (
                  <>
                    { (
                      <Typography variant="caption" sx={{ mt: 1, fontStyle: 'italic' }}> 
                        par {assignment.user.first_name} {assignment.user.last_name}
                      </Typography>
                    )}
                  </>
                )}
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      {view === 'weekly' && (
        <TableContainer component={Paper} sx={{ mt: 4 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Place</TableCell>
                {weekDays.map(day => (
                  <TableCell key={day.toString()} align="center">
                    <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{format(day, 'eeee', { locale: frLocale })}</Typography>
                    <Typography variant="caption">{format(day, 'dd/MM')}</Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {spots.sort((a, b) => a.spot_number.localeCompare(b.spot_number, undefined, { numeric: true })).map(spot => (
                <TableRow key={spot.id}>
                  <TableCell component="th" scope="row">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography fontWeight="bold">{spot.spot_number}</Typography>
                      {spot.has_electric_charger && <Tooltip title="Borne électrique"><ElectricCarIcon color="action" /></Tooltip>}
                      {spot.is_pmr_accessible && <Tooltip title="Accès PMR"><AccessibleIcon color="action" /></Tooltip>}
                    </Box>
                  </TableCell>
                  {weekDays.map(day => {
                    const assignment = assignmentsBySpotAndDate.get(`${spot.id}-${format(day, 'yyyy-MM-dd')}`);
                    const isMyReservation = assignment?.user_id === user?.id;
                    return (
                      <TableCell
                        key={day.toString()}
                        align="center"
                        sx={{
                          backgroundColor: isMyReservation ? 'primary.light' : (assignment ? 'error.light' : 'success.light'),
                          color: 'white',
                          cursor: 'pointer',
                        }}
                        onClick={() => handleSpotClick(spot, assignment)}
                      >
                        {assignment ? (
                          <Tooltip title={isAdmin ? `par ${assignment.user.first_name} ${assignment.user.last_name}` : (isMyReservation ? 'Ma réservation' : 'Occupé')}>
                            <Box>
                              <PersonIcon />
                            </Box>
                          </Tooltip>
                        ) : (
                          'Libre'
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Reservation Confirmation Modal */}
      <Dialog open={isModalOpen} onClose={handleCloseModal}>
        <DialogTitle>Confirmer la réservation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Voulez-vous vraiment réserver la place <strong>{spotToReserve?.spot_number}</strong> pour le <strong>{selectedDate ? format(selectedDate, 'dd/MM/yyyy') : ''}</strong> ?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Annuler</Button>
          <Button onClick={handleConfirmReservation} variant="contained" autoFocus>
            Confirmer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancellation Confirmation Modal */}
      <Dialog open={isCancelModalOpen} onClose={handleCloseCancelModal}>
        <DialogTitle>Confirmer l'annulation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Voulez-vous vraiment libérer la place <strong>{assignmentToCancel?.parking_spot.spot_number}</strong> pour le <strong>{selectedDate ? format(selectedDate, 'dd/MM/yyyy') : ''}</strong> ?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCancelModal}>Non</Button>
          <Button onClick={handleConfirmCancellation} variant="contained" color="error" autoFocus>
            Oui, libérer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Motorcycle Confirmation Modal */}
      <Dialog open={isMotorcycleModalOpen} onClose={handleCloseMotorcycleModal}>
        <DialogTitle>Confirmation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {motorcycleAction === 'checkin'
              ? `Voulez-vous signaler votre présence en moto pour le ${selectedDate ? format(selectedDate, 'dd/MM/yyyy') : ''} ?`
              : `Voulez-vous annuler votre présence en moto pour le ${selectedDate ? format(selectedDate, 'dd/MM/yyyy') : ''} ?`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMotorcycleModal}>Annuler</Button>
          <Button onClick={handleConfirmMotorcycleAction} variant="contained" autoFocus>
            Confirmer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Dashboard;